import { useEffect, useState } from 'react';
import { get } from '../api';
import type { Excepcion } from '../tipos';
import { Gravedad, Vacio } from '../components/ui';
import { formatear } from '../components/GraficoPlan';

const NOMBRES: Record<string, string> = {
  late: 'Llega tarde',
  'no-bom': 'Sin escandallo',
  'no-vendor': 'Sin proveedor',
  'below-safety': 'Por debajo del mínimo',
  'negative-stock': 'Stock negativo',
  excess: 'Sobra',
  'reschedule-in': 'Hay que adelantar',
  'reschedule-out': 'Se puede retrasar',
  'no-lead-time': 'Sin plazo de entrega',
  'orphan-demand': 'Componente desconocido',
};

/**
 * Lo que el plan no puede resolver solo.
 *
 * Ordenadas por gravedad, no por artículo: lo que bloquea arriba. Las que
 * bloquean significan que el plan está INCOMPLETO en algún punto — pide de
 * menos, o no explota un escandallo — y eso hay que verlo antes de fiarse de
 * los números de al lado.
 */
export function Excepciones({
  runId,
  onVerArticulo,
}: {
  runId: string;
  onVerArticulo: (codigo: string, almacen: string) => void;
}): JSX.Element {
  const [filas, setFilas] = useState<Excepcion[]>([]);
  const [gravedad, setGravedad] = useState('');
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    const q = gravedad ? `?severity=${gravedad}` : '';
    void get<{ exceptions: Excepcion[] }>(`/runs/${runId}/exceptions${q}`)
      .then((r) => vigente && setFilas(r.exceptions))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [runId, gravedad]);

  return (
    <div>
      <div className="filtros">
        <select value={gravedad} onChange={(e) => setGravedad(e.target.value)}>
          <option value="">Todas</option>
          <option value="blocker">Solo las que bloquean</option>
          <option value="warning">Solo avisos</option>
          <option value="info">Solo informativas</option>
        </select>
        <span className="sutil">{filas.length} incidencia(s)</span>
      </div>

      <div className="tarjeta" style={{ padding: 0 }}>
        <div className="tabla-scroll">
          <table>
            <thead>
              <tr>
                <th>Gravedad</th>
                <th>Tipo</th>
                <th>Artículo</th>
                <th>Qué pasa</th>
                <th className="num">Días</th>
                <th className="num">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((e) => (
                <tr key={e.id}>
                  <td><Gravedad severity={e.severity} /></td>
                  <td>{NOMBRES[e.kind] ?? e.kind}</td>
                  <td>
                    {e.item_code ? (
                      <button
                        onClick={() => onVerArticulo(e.item_code, e.warehouse)}
                        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--serie-1)', cursor: 'pointer' }}
                      >
                        {e.item_code}
                      </button>
                    ) : (
                      '—'
                    )}
                    {e.warehouse ? <div className="sutil">almacén {e.warehouse}</div> : null}
                  </td>
                  <td style={{ maxWidth: 640 }}>{e.message}</td>
                  <td className="num mono">{e.days ?? '—'}</td>
                  <td className="num mono">{e.quantity === null ? '—' : formatear(e.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cargando ? <Vacio>Cargando…</Vacio> : filas.length === 0 ? <Vacio>Ninguna incidencia. El plan se ha calculado entero.</Vacio> : null}
      </div>
    </div>
  );
}
